import { FilterParamsBase } from "@/types/filter.types";
import { ensureArray } from "@/utils/gallery/ensureArray";
import { isEqual } from "@/utils/gallery/isEqual";
import debounce from "lodash.debounce";
import { computed, onBeforeUnmount, ref, UnwrapRef, watch } from "vue";
import { LocationQueryValue, useRoute, useRouter } from "vue-router";

const URL_UPDATE_DEBOUNCE = 500;

const germanSearchUtils = {
  toAscii: (str: string): string =>
    str
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/Ä/g, "Ae")
      .replace(/Ö/g, "Oe")
      .replace(/Ü/g, "Ue"),

  normalizeSpacing: (str: string): string => str.replace(/\s+/g, " ").trim(),

  createSearchParams: (query: string) => {
    const normalized = germanSearchUtils.normalizeSpacing(query);
    return {
      query: normalized,
      queryAscii: germanSearchUtils.toAscii(normalized),
    };
  },
};

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    console.warn("Failed to decode URL parameter:", value);
    return value;
  }
};

export interface FilterOptions<T> {
  defaultParams: T;
  urlUpdateDebounceTime?: number;
  normalizeSearch?: boolean;
  onSearch?: (params: T) => void;
}

export const useFilter = <T extends FilterParamsBase>(
  options: FilterOptions<T>
) => {
  const router = useRouter();
  const route = useRoute();

  const urlDebounce = options.urlUpdateDebounceTime || URL_UPDATE_DEBOUNCE;

  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const initializeParams = (): T => {
    const initialParams = { ...options.defaultParams };
    Object.keys(initialParams).forEach((key) => {
      if (key in route.query) {
        const routeValue = route.query[key];
        if (Array.isArray(initialParams[key as keyof T])) {
          (initialParams[key as keyof T] as unknown[]) = ensureArray(
            routeValue as string[]
          ).map((value) => safeDecodeURIComponent(String(value)));
        } else if (typeof initialParams[key as keyof T] === "number") {
          (initialParams[key as keyof T] as unknown) =
            Number(routeValue) || initialParams[key as keyof T];
        } else {
          (initialParams[key as keyof T] as unknown) = safeDecodeURIComponent(
            String(routeValue)
          );
        }
      }
    });

    return initialParams;
  };

  const initialParams = ref<T>({ ...options.defaultParams });
  const params = ref<T>(initializeParams());

  const hasActiveFilters = computed(() => {
    return Object.keys(params.value).some((key) => {
      if (key === "page" || key === "limit") return false;
      return !isEqual(
        (params.value as T)[key as keyof T],
        (initialParams.value as T)[key as keyof T]
      );
    });
  });

  // Watch for params changes to trigger search
  watch(
    () => params.value,
    (newParams) => {
      if (options.onSearch) {
        options.onSearch(newParams);
      }
    },
    { deep: true, immediate: true }
  );

  const updateUrl = async () => {
    const queryParams: Record<string, string | string[]> = {};

    try {
      for (const [key, value] of Object.entries(params.value as T)) {
        if (value !== null && value !== undefined && value !== "") {
          if (typeof value === "string" && value.trim() !== "") {
            queryParams[key] = safeDecodeURIComponent(value);
          } else if (Array.isArray(value) && value.length > 0) {
            queryParams[key] = value.map((v) =>
              safeDecodeURIComponent(String(v))
            );
          } else if (typeof value !== "string" && !Array.isArray(value)) {
            queryParams[key] = String(value);
          }
        }
      }

      await router.replace({ query: queryParams });
      error.value = null;
    } catch (e) {
      console.error("Error updating URL:", e);
      error.value = "Failed to update filters";
    }
  };

  const debouncedUpdateUrl = debounce(updateUrl, urlDebounce);

  const updateFilter = async <K extends keyof T>(key: K, value: T[K]) => {
    params.value = { ...(params.value as T), [key]: value } as UnwrapRef<T>;
    debouncedUpdateUrl();
  };

  const groupParamsUpdate = (selectedValues: Partial<T>) => {
    params.value = {
      ...params.value,
      ...selectedValues,
      page: 1,
    } as UnwrapRef<T>;
    debouncedUpdateUrl();
  };

  const resetFilters = () => {
    params.value = { ...options.defaultParams } as UnwrapRef<T>;
    debouncedUpdateUrl();
  };

  const search = (query: string) => {
    params.value = { ...(params.value as T), query, page: 1 } as UnwrapRef<T>;
    debouncedUpdateUrl();
  };

  // Watch for route changes
  watch(
    () => route.query,
    () => {
      const queryKeys = Object.keys(route.query);
      const newParams = { ...options.defaultParams } as Record<
        string,
        string | LocationQueryValue[]
      >;

      queryKeys.forEach((key) => {
        if (key in newParams) {
          const queryValue = route.query[key];
          if (!queryValue) return;
          newParams[key] = safeDecodeURIComponent(String(queryValue));
        }
      });

      params.value = newParams as UnwrapRef<T>;
    },
    { deep: true }
  );

  onBeforeUnmount(() => {
    debouncedUpdateUrl.cancel();
  });

  return {
    hasActiveFilters,
    params,
    isLoading,
    error,
    groupParamsUpdate,
    updateUrl: debouncedUpdateUrl,
    updateFilter,
    resetFilters,
    search,
    utils: germanSearchUtils,
  };
};
